import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import Dialog from '../Dialog';
import Input from '../Input';
import Button from '../Button';
import RestorativeProgramSelection from './RestorativeProgramSelection';

import Restorative from '../../services/Restorative';
import AutocompleteBubble from '../AutocompleteBubble';

import ExlamationCircleIcon from '../../assets/icons/exclaimation-circle.svg';

const CLICK_EVENT_NAME = 'Care_CareUI_Programs_Click';
const SCREEN_BREAKPOINT = '1100px';

interface RestorativeProgram {
  id: number;
  name: string;
}

type Player = {
  id: number;
  name: string;
};

const DialogButton = styled(Button)`
  padding: 15px 60px;
`;

const CancelButton = styled.button(({ theme }) => {
  return `
    font-size: ${theme.type.size.small};
    color: ${theme.color.grayscale[0]};
    background: none;
    border: none;
    cursor: pointer;
    padding: 15px 40px;
    margin-right: 16px;
    `;
});

const ArchiveButton = styled.button(({ theme }) => {
  return `
    font-size: ${theme.type.size.small};
    color: ${theme.color.primary.dark};
    background: none;
    border: 1px solid ${theme.color.primary.dark};
    border-radius: 8px;
    cursor: pointer;
    padding: 15px 24px;
    line-height: 1;
    &:hover {
      opacity: 0.85;
    }
  `;
});

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FooterPrimaryActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
`;

const GeneralErrorList = styled.div(({ theme }) => {
  return `
    margin-top: 10px;
    font-size: ${theme.type.size.regular};
    color: ${theme.color.primary.dark};
  `;
});

const Title = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.larger};
    font-weight: bold;
    margin-bottom: 20px;

    @media (max-width: ${SCREEN_BREAKPOINT}) {
      font-size: ${theme.type.size.large}
    }
  `;
});

const InputVariant = styled(Input)`
  padding: 0.625rem;
`;

const Content = styled.div`
  width: 800px;
  @media (max-width: ${SCREEN_BREAKPOINT}) {
    width: 700px;
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const InputContainer = styled.div`
  position: relative;
  text-align: left;
  width: 300px;
  margin-bottom: 13px;

  @media (max-width: ${SCREEN_BREAKPOINT}) {
    width: 100%;
  }
`;

const Section = styled.div(({ theme }) => {
  return `
    border: 1px solid ${theme.color.grayscale[700]};
    border-radius: 10px;
    padding: 10px 45px;
    width: 726px;
    margin: 0 auto;
    margin-bottom: 40px;

    @media (max-width: ${SCREEN_BREAKPOINT}) {
      width: 100%;
    }
  `;
});

const InputSectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 35px;
  min-width: 400px;
  justify-content: center;

  @media (max-width: ${SCREEN_BREAKPOINT}) {
    min-width: inherit;
    display: contents;
    width: 100%;
  }
`;

const SectionTitle = styled.div(({ theme }) => {
  return `
    font-weight: bold;
    font-size: ${theme.type.size.large};
    margin-bottom: 15px;
    text-align: left;
    position: relative;
    margin-top: 10px;
  `;
});

const ButtonWrap = styled.div`
  margin-right: 10px;
  display: inline-block;
  cursor: pointer;
`;

const MessageWrap = styled.div`
  height: 21px;
  margin-botom: 13px;
`;

const DupMessage = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[14]};
    text-align: left;
  `;
});

const ExclamationIcon = styled.img`
  display: inline-block;
  width: 22px;
  height: 22px;
  position: relative;
  top: -2px;
  margin-eight: 6px;
`;

const ArchiveConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ArchiveConfirmPanel = styled.div(({ theme }) => {
  const { color, shadow } = theme;
  return `
    background: ${color.grayscale[900]};
    border-radius: 15px;
    padding: 28px 32px;
    max-width: 440px;
    width: 100%;
    box-shadow: ${shadow.regular};
    text-align: left;
    color: ${color.grayscale[0]};
  `;
});

const ArchiveConfirmTitle = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.large};
    font-weight: bold;
    margin-bottom: 12px;
  `;
});

const ArchiveConfirmBody = styled.p(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    margin: 0 0 24px;
    line-height: 1.45;
    color: ${theme.color.grayscale[0]};
  `;
});

const ArchiveConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const SavingRow = styled.div`
  margin-top: 8px;
  font-size: 1rem;
`;

export type FormErrors = {
  firstName?: string;
  lastName?: string;
  login?: string;
  general?: string[];
};

export type RestorativeResidentData = {
  id: number;
  firstName: string;
  lastName: string;
  programs: RestorativeProgram[];
};

interface AddPlayerDialogProps {
  editPlayer: false | RestorativeResidentData;
  submitCallback?: (
    firstName: string,
    lastName: string,
    shouldAddHomePlayer: boolean,
    login: string,
    password: string
  ) => void;
  convertResidentCallback: (playerId: number, programs: RestorativeProgram[]) => void;
  addResidentCallback: (firstName: string, lastName: string, programs: RestorativeProgram[]) => void;
  editResidentCallback: (id: number, firstName: string, lastName: string, programs: RestorativeProgram[]) => void;
  removeFromRestorativeCallback?: (id: number) => void;
  backendErrors?: FormErrors;
  isOpen: boolean;
  closeCallback: () => void;
  saving: boolean;
  /** Shown with the spinner while `saving` is true (e.g. archive vs save). */
  savingMessage?: string;
  mode?: string;
  restorativePrograms?: RestorativeProgram[];
}

interface Name {
  firstName: string;
  lastName: string;
}

function splitFullName(fullName: string): Name {
  const [firstName, ...lastNameArray] = fullName.split(' ');
  const lastName = lastNameArray.join(' ');

  return {
    firstName,
    lastName,
  };
}

const formatRestorativePrograms = (restorativePrograms: RestorativeProgram[] | undefined): RestorativeProgram[] => {
  if (!restorativePrograms) {
    return [];
  }
  return restorativePrograms.map((program) => {
    return {
      id: program.id,
      name: program.name,
    };
  });
};

const AddPlayerDialog: FC<AddPlayerDialogProps> = ({
  isOpen,
  closeCallback,
  saving,
  savingMessage,
  backendErrors = {},
  editPlayer,
  convertResidentCallback,
  addResidentCallback,
  editResidentCallback,
  removeFromRestorativeCallback,
  restorativePrograms,
}) => {
  const [shouldHandleSearch, setShouldHandleSearch] = useState(false);
  const [firstName, setFirstName] = useState(editPlayer !== false ? editPlayer.firstName : '');
  const [lastName, setLastName] = useState(editPlayer !== false ? editPlayer.lastName : '');
  const [canSubmit, setCanSubmit] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState<RestorativeProgram[]>(
    editPlayer !== false ? formatRestorativePrograms(editPlayer.programs) : []
  );

  const [nameDupCheckTimeout, setNameDupCheckTimeout] = useState<NodeJS.Timeout | boolean>(false);
  const [showDupMessage, setShowDupMessage] = useState(false);

  const [playerSearchTimeout, setPlayerSearchTimeout] = useState<NodeJS.Timeout | boolean>(false);
  const [playerSearchResult, setPlayerSearchResult] = useState([]);
  const [selectedPlayerIdToTag, setSelectedPlayerIdToTag] = useState<number>(-1);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const allErrors = Object.assign({}, errors, backendErrors);

  // isQuiet can be used to validate the form without triggering errors.
  const validate = (isQuiet = false) => {
    const newErrors: FormErrors = {};
    let valid = true;

    if (firstName.trim().length < 1) {
      newErrors.firstName = 'First Name cannot be empty';
      valid = false;
    }

    if (lastName.trim().length < 1) {
      newErrors.lastName = 'Last Name cannot be empty';
      valid = false;
    }

    if (!isQuiet) {
      setErrors(newErrors);
    }

    setCanSubmit(valid);

    return valid;
  };

  const checkNameDup = async () => {
    setShowDupMessage(false);
    if (editPlayer || selectedPlayerIdToTag !== -1) {
      return;
    }

    if (nameDupCheckTimeout) {
      clearTimeout(nameDupCheckTimeout as NodeJS.Timeout);
    }

    setNameDupCheckTimeout(
      setTimeout(async () => {
        const response = await Restorative.checkForPlayerNameDup(firstName + ' ' + lastName);

        if (response.dup_found) {
          setShowDupMessage(true);
        } else {
          setShowDupMessage(false);
        }
        setNameDupCheckTimeout(false);
      }, 2000)
    );
  };

  const submit = () => {
    const valid = validate();

    if (valid) {
      if (editPlayer !== false) {
        mixpanel.track(CLICK_EVENT_NAME, {
          Action: 'submit',
          source: 'Edit',
        });
        editResidentCallback(editPlayer.id, firstName, lastName, selectedPrograms);
      } else {
        if (selectedPlayerIdToTag !== -1) {
          convertResidentCallback(selectedPlayerIdToTag, selectedPrograms);
        } else {
          addResidentCallback(firstName, lastName, selectedPrograms);
        }

        mixpanel.track(CLICK_EVENT_NAME, {
          Action: 'submit',
          source: selectedPlayerIdToTag !== -1 ? 'RESTORESkills' : 'New',
        });
      }
    }
  };

  const openArchiveConfirm = () => {
    if (editPlayer === false || !removeFromRestorativeCallback || saving) {
      return;
    }
    setShowArchiveConfirm(true);
  };

  const closeArchiveConfirm = () => {
    setShowArchiveConfirm(false);
  };

  const confirmArchiveFromRestorative = () => {
    if (editPlayer === false || !removeFromRestorativeCallback) {
      return;
    }
    mixpanel.track(CLICK_EVENT_NAME, {
      Action: 'archive',
      source: 'Edit',
    });
    setShowArchiveConfirm(false);
    removeFromRestorativeCallback(editPlayer.id);
  };

  const search = async () => {
    if (!shouldHandleSearch) {
      return;
    }

    if (!editPlayer) {
      if (playerSearchTimeout) {
        clearTimeout(playerSearchTimeout as NodeJS.Timeout);
      }

      if (firstName === '') {
        setPlayerSearchResult([]);
        setPlayerSearchTimeout(false);
        return;
      }
      setPlayerSearchTimeout(
        setTimeout(async () => {
          setPlayerSearchResult([]);
          let query = firstName;
          if (lastName !== '') {
            query += ' ' + lastName;
          }
          const response = await Restorative.searchPlayers({ query: query, limit: 5, restorative: false });

          setPlayerSearchResult(
            response.players.map((player: Player) => {
              return {
                value: player.id,
                label: player.name,
              };
            })
          );
          setPlayerSearchTimeout(false);
        }, 1000)
      );
    }
  };

  useEffect(() => {
    search();
  }, [firstName, lastName]);

  useEffect(() => {
    validate(true);

    checkNameDup();
  }, [firstName, lastName]);

  useEffect(() => {
    if (!isOpen) {
      setShowArchiveConfirm(false);
    }
  }, [isOpen]);

  return (
    <Dialog isOpen={isOpen} canClose={false} closeCallback={closeCallback}>
      <Content>
        <Title>{editPlayer !== false ? 'Edit Resident and Programs' : 'Add a Resident'}</Title>

        <Section>
          <SectionTitle>Personal Information</SectionTitle>

          <InputSectionContainer>
            <InputContainer>
              <InputVariant
                label="First Name"
                hideOptional={true}
                required={true}
                value={firstName}
                onChange={(e) => {
                  setShouldHandleSearch(true);
                  setFirstName(e.target.value);
                  setSelectedPlayerIdToTag(-1);
                }}
                invalid={allErrors.firstName !== undefined}
                errors={allErrors.firstName}
              />
              {playerSearchResult.length > 0 && (
                <AutocompleteBubble
                  options={playerSearchResult}
                  onSelect={(player) => {
                    setShouldHandleSearch(false);
                    const newName = splitFullName(player.label);
                    setFirstName(newName.firstName);
                    setLastName(newName.lastName);
                    setSelectedPlayerIdToTag(parseInt(player.value));
                  }}
                />
              )}
            </InputContainer>

            <InputContainer>
              <InputVariant
                label="Last Name"
                hideOptional={true}
                required={true}
                onChange={(e) => {
                  setShouldHandleSearch(true);
                  setLastName(e.target.value);
                  setSelectedPlayerIdToTag(-1);
                }}
                value={lastName}
                invalid={allErrors.lastName !== undefined}
                errors={allErrors.lastName}
              />
            </InputContainer>
          </InputSectionContainer>
          <MessageWrap>
            {showDupMessage && (
              <DupMessage>
                <ExclamationIcon src={ExlamationCircleIcon}></ExclamationIcon> This name is already on our records. To
                avoid duplications, choose from the existing residents list
              </DupMessage>
            )}
          </MessageWrap>
        </Section>

        <Section>
          <SectionTitle>Restorative Programs</SectionTitle>

          <RestorativeProgramSelection
            programs={formatRestorativePrograms(restorativePrograms) || []}
            selected={selectedPrograms}
            onUpdate={(selectedPrograms) => {
              setSelectedPrograms(selectedPrograms);
            }}
          />
        </Section>

        {!saving && (
          <FooterActions>
            <div>
              {editPlayer !== false && removeFromRestorativeCallback && (
                <ArchiveButton type="button" disabled={saving} onClick={openArchiveConfirm}>
                  Archive
                </ArchiveButton>
              )}
            </div>
            <FooterPrimaryActions>
              <ButtonWrap>
                <CancelButton
                  onClick={() => {
                    closeCallback();
                    mixpanel.track(CLICK_EVENT_NAME, { Action: 'cancel' });
                  }}
                >
                  Cancel
                </CancelButton>
              </ButtonWrap>

              <ButtonWrap>
                <DialogButton
                  disabled={!canSubmit}
                  onClick={() => {
                    submit();
                  }}
                >
                  {editPlayer !== false ? 'Save Changes' : 'Add Resident'}
                </DialogButton>
              </ButtonWrap>
            </FooterPrimaryActions>
          </FooterActions>
        )}

        {saving && (
          <SavingRow>
            {savingMessage ?? 'Saving...'} <i className="fa fa-spinner fa-spin" aria-hidden />{' '}
          </SavingRow>
        )}

        {allErrors.general && (
          <GeneralErrorList>
            {allErrors.general.map((error) => {
              return <div>{error}</div>;
            })}
          </GeneralErrorList>
        )}

        {showArchiveConfirm && editPlayer !== false && (
          <ArchiveConfirmOverlay
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeArchiveConfirm();
              }
            }}
          >
            <ArchiveConfirmPanel
              role="dialog"
              aria-modal="true"
              aria-labelledby="archive-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <ArchiveConfirmTitle id="archive-confirm-title">Remove from restorative list?</ArchiveConfirmTitle>
              <ArchiveConfirmBody>
                This resident will be removed from the restorative list and unassigned from restorative programs.
              </ArchiveConfirmBody>
              <ArchiveConfirmActions>
                <CancelButton type="button" onClick={closeArchiveConfirm}>
                  Cancel
                </CancelButton>
                <DialogButton type="button" onClick={confirmArchiveFromRestorative}>
                  Remove
                </DialogButton>
              </ArchiveConfirmActions>
            </ArchiveConfirmPanel>
          </ArchiveConfirmOverlay>
        )}
      </Content>
    </Dialog>
  );
};

export default AddPlayerDialog;
